// Small currency helpers to keep formatting consistent across the app.
// We prefer Intl.NumberFormat because it handles locale grouping and symbols.

export function normalizeCurrencyCode(currency) {
  if (!currency) return null;
  const code = String(currency).trim().toUpperCase();
  if (!code) return null;
  // Accept only ISO-4217-like 3-letter currency codes.
  // Filters placeholders like "currency", "none", etc.
  if (!/^[A-Z]{3}$/.test(code)) return null;
  return code;
}

export function formatCurrencyAmount(
  value,
  currency,
  {
    locale = 'es-ES',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    // display:
    // - 'code': ISO 4217 code (USD, ARS, EUR) - recommended when multiple "$" currencies exist
    // - 'symbol': currency symbol (can be ambiguous: $ for USD/ARS/CAD...)
    display = 'code',
    currencyDisplay = 'narrowSymbol',
  } = {}
) {
  if (value === null || value === undefined || value === '') return 'N/A';

  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return 'N/A';

  const code = normalizeCurrencyCode(currency) || 'USD';

  try {
    const formatted = num.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    });
    if (display === 'symbol') {
      // Keep prefix style consistent with existing UI ($123,45),
      // while still picking the right symbol for the currency code.
      const symbol = getCurrencySymbol(code, { locale, currencyDisplay }) || code;
      return `${symbol}${formatted}`;
    }

    // Default: ISO currency code
    return `${code} ${formatted}`;
  } catch (_e) {
    // Fallback if Intl rejects the currency code.
    const formatted = num.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    });
    return `${code} ${formatted}`;
  }
}

function getCurrencySymbol(currency, { locale = 'es-ES', currencyDisplay = 'narrowSymbol' } = {}) {
  const code = normalizeCurrencyCode(currency);
  if (!code) return '';
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbolPart = parts.find((p) => p.type === 'currency');
    return symbolPart?.value || code;
  } catch (_e) {
    return code;
  }
}

